import { Component, OnInit, OnDestroy, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, switchMap } from 'rxjs/operators';
import { ProjectService } from '../../../services/project.service';
import { GroupService } from '../../../services/group.service';
import { ProfileService } from '../../../services/profile.service';
import { Project } from '../../../dtos/project';
import { ProjectRole } from '../../../dtos/project-role';
import { Group } from '../../../dtos/group';
import { UserInfo } from '../../../dtos/user-info';
import { PaginatedResponse } from '../../../dtos/paginated';
import { ProjectCardComponent } from '../project-card/project-card.component';
import { ProjectCreateModalComponent } from '../../modals/project-create-modal/project-create-modal.component';
import { Router, ActivatedRoute } from '@angular/router';
import { ConfirmModalComponent } from '../../modals/confirm-modal/confirm-modal.component';
import { formatStageState, stageToChildRoute } from '../../../dtos/project-state';

const PAGE_SIZE = 25;
const DEBOUNCE_MS = 300;

@Component({
  selector: 'app-projects-overview',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ProjectCardComponent,
    ProjectCreateModalComponent,
    ConfirmModalComponent,
  ],
  templateUrl: './projects-overview.component.html',
  styleUrls: ['./projects-overview.component.scss'],
})
export class ProjectsOverviewComponent implements OnInit, OnDestroy {
  private projectService: ProjectService;
  private groupService: GroupService;
  private profileService: ProfileService;
  private router: Router;
  private route: ActivatedRoute;

  readonly projectsPage = signal<PaginatedResponse<Project> | null>(null);
  readonly searchQuery = signal('');
  readonly selectedGroup = signal('');
  readonly selectedState = signal('');
  readonly selectedDateRange = signal('');
  readonly currentPage = signal(0);
  readonly totalPages = signal(0);
  readonly loading = signal(true);
  readonly errorMessage = signal('');
  private readonly search$ = new Subject<{
    q: string;
    groupId: string;
    state: string;
    dateRange: string;
    page: number;
  }>();
  private userInfo: UserInfo | null = null;
  private sub?: Subscription;

  readonly projects = computed(() => this.projectsPage()?.items ?? []);
  readonly totalItems = computed(() => this.projectsPage()?.totalItems ?? 0);

  groups: Group[] = [];
  userRoles: Map<string, ProjectRole> = new Map<string, ProjectRole>();

  projectCreateModalOpen = false;

  showLeaveProjectConfirm = false;
  projectToLeave: Project | null = null;
  leaveProjectLoading = false;
  leaveProjectError: string | null = null;

  states = [
    'SETUP_INGESTION',
    'TITLE_ABSTRACT_SCREENING',
    'FULL_TEXT_RETRIEVAL',
    'FULL_TEXT_SCREENING',
    'DATA_EXTRACTION',
    'COMPLETED',
  ];
  dateRanges = [
    { label: 'All Time', value: '' },
    { label: 'Last 7 days', value: '7days' },
    { label: 'Last 30 days', value: '30days' },
    { label: 'Last 3 months', value: '3months' },
    { label: 'This year', value: 'year' },
  ];

  constructor(
    projectService: ProjectService,
    groupService: GroupService,
    profileService: ProfileService,
    router: Router,
    route: ActivatedRoute,
  ) {
    this.projectService = projectService;
    this.groupService = groupService;
    this.profileService = profileService;
    this.router = router;
    this.route = route;
  }

  ngOnInit(): void {
    const searchSub = this.search$
      .pipe(
        debounceTime(DEBOUNCE_MS),
        switchMap(({ q, groupId, state, dateRange, page }) => {
          this.currentPage.set(page);
          return this.projectService.getProjectsPaginated(
            page,
            PAGE_SIZE,
            q || undefined,
            groupId || undefined,
            state || undefined,
            dateRange || undefined,
          );
        }),
      )
      .subscribe({
        next: (res) => {
          this.projectsPage.set(res);
          this.totalPages.set(res.totalPages);
          this.currentPage.set(res.currentPage);
          this.loading.set(false);
          this.errorMessage.set('');
        },
        error: (err) => {
          this.errorMessage.set(err?.error?.message ?? 'Could not load projects.');
          this.loading.set(false);
        },
      });

    this.sub = new Subscription();
    this.sub.add(searchSub);

    this.loadData();

    this.profileService.getCurrentUser().subscribe({
      next: (user) => {
        this.userInfo = user;
      },
      error: () => {},
    });

    this.groupService.getGroups().subscribe({
      next: (groups) => {
        this.groups = groups;
      },
      error: () => {},
    });

    this.route.queryParams.subscribe((params) => {
      const groupParam = params['group'] || '';
      if (this.selectedGroup() !== groupParam) {
        this.selectedGroup.set(groupParam);
        this.currentPage.set(0);
        this.triggerSearch();
      }
    });
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  private loadData(): void {
    this.loading.set(true);
    this.errorMessage.set('');
    this.projectService
      .getProjectsPaginated(
        0,
        PAGE_SIZE,
        undefined,
        this.selectedGroup() || undefined,
        this.selectedState() || undefined,
        this.selectedDateRange() || undefined,
      )
      .subscribe({
        next: (res) => {
          this.projectsPage.set(res);
          this.totalPages.set(res.totalPages);
          this.currentPage.set(res.currentPage);
          this.loading.set(false);
        },
        error: (err) => {
          this.errorMessage.set(err?.error?.message ?? 'Could not load projects.');
          this.loading.set(false);
        },
      });
  }

  private triggerSearch(): void {
    this.search$.next({
      q: this.searchQuery(),
      groupId: this.selectedGroup(),
      state: this.selectedState(),
      dateRange: this.selectedDateRange(),
      page: this.currentPage(),
    });
  }

  onSearchChange(value: string): void {
    this.searchQuery.set(value);
    this.currentPage.set(0);
    this.triggerSearch();
  }

  onGroupChange(value: string): void {
    this.selectedGroup.set(value);
    this.currentPage.set(0);
    this.triggerSearch();
  }

  onStateChange(value: string): void {
    this.selectedState.set(value);
    this.currentPage.set(0);
    this.triggerSearch();
  }

  onDateRangeChange(value: string): void {
    this.selectedDateRange.set(value);
    this.currentPage.set(0);
    this.triggerSearch();
  }

  goToPage(page: number): void {
    if (page < 0 || page >= this.totalPages()) return;
    this.currentPage.set(page);
    this.triggerSearch();
  }

  canPrev(): boolean {
    return this.currentPage() > 0;
  }

  canNext(): boolean {
    return this.currentPage() < this.totalPages() - 1;
  }

  getUserRole(projectId: string): ProjectRole | undefined {
    return this.userRoles.get(projectId);
  }

  formatState(state: string): string {
    if (state === 'SETUP_INGESTION') return 'Setup / Ingestion';
    return formatStageState(state);
  }

  canEditProject(project: Project): boolean {
    const role = this.userRoles.get(project.id);
    return role?.role === 'PI' || false;
  }

  viewProjectDetails(project: Project): void {
    const route = stageToChildRoute(project.state);
    const segments = route ? ['/projects', project.id, route] : ['/projects', project.id];
    this.router.navigate(segments);
  }

  editProject(project: Project): void {
    this.router.navigate(['/projects', project.id, 'edit']);
  }

  clearFilters(): void {
    this.searchQuery.set('');
    this.selectedGroup.set('');
    this.selectedState.set('');
    this.selectedDateRange.set('');
    this.currentPage.set(0);
    this.triggerSearch();
  }

  hasActiveFilters(): boolean {
    return (
      this.searchQuery() !== '' ||
      this.selectedGroup() !== '' ||
      this.selectedState() !== '' ||
      this.selectedDateRange() !== ''
    );
  }

  openProjectCreateModal(): void {
    this.projectCreateModalOpen = true;
  }

  onProjectCreated(project: Project): void {
    this.userRoles.set(project.id, new ProjectRole(project.id, project.userRole || 'PI'));
    this.currentPage.set(0);
    this.triggerSearch();
  }

  get leaveProjectMessage(): string {
    return this.projectToLeave
      ? `Are you sure you want to leave the project "${this.projectToLeave.title}"?`
      : '';
  }

  onLeaveProject(project: Project): void {
    this.projectToLeave = project;
    this.leaveProjectError = null;
    this.showLeaveProjectConfirm = true;
  }

  confirmLeaveProject(): void {
    if (!this.projectToLeave || this.leaveProjectLoading) {
      return;
    }

    const project = this.projectToLeave;
    this.leaveProjectLoading = true;
    this.leaveProjectError = null;

    this.projectService.leaveProject(project.id).subscribe({
      next: () => {
        this.showLeaveProjectConfirm = false;
        this.projectToLeave = null;
        this.leaveProjectLoading = false;
        this.triggerSearch();
      },
      error: (error) => {
        this.leaveProjectError = this.getLeaveProjectErrorMessage(error);
        this.showLeaveProjectConfirm = false;
        this.leaveProjectLoading = false;
      },
    });
  }

  cancelLeaveProject(): void {
    this.showLeaveProjectConfirm = false;
    this.projectToLeave = null;
    this.leaveProjectLoading = false;
    this.leaveProjectError = null;
  }

  private getLeaveProjectErrorMessage(error: any): string {
    return (
      error?.error?.message ??
      error?.error?.detail ??
      error?.message ??
      'An error occurred while leaving the project.'
    );
  }
}
